/**
 * 기록 한 건 — 피드의 카드.
 *
 * 장소를 카드 머리에 답니다. 사진만 있고 글이 없는 기록이 많을 텐데,
 * 장소가 없으면 그런 기록은 읽히지 않습니다.
 *
 * 댓글은 접어 둡니다. 다 펼치면 목록이 길어져 사진이 안 보입니다.
 * 접었다 펴는 방식은 모임 후기(MeetingPostsSection)와 같습니다.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionList, ActionRow, PhotoGrid, PhotoViewerModal, Sheet, StarRating, SvgBase } from 'components/ds';
import { IcChat, IcHeart, IcPin } from 'components/common/icons/EmojiIcons';
import { PlaceReviewRepository } from 'repository/placeReviewRepository';
import { unwrapList } from 'common/utils/apiResponse';
import { formatRelative } from 'common/utils/datetime';
import type { JournalEntry } from 'hooks/useMeetingJournal';
import styles from './JournalEntryCard.module.css';

/** 카드에 한 번에 펼치는 최대 장수. 넘치면 마지막 칸에 +N 으로 접고,
 *  눌러서 크게 보기로 들어가면 나머지까지 넘겨 볼 수 있습니다. */
const PHOTO_MAX = 9;

interface Comment {
    commentId: number;
    nickname?: string | null;
    content: string;
    mine?: boolean | null;
}

interface Props {
    entry: JournalEntry;
    /** 일차별 보기 — Day 제목이 이미 말하고 있어 머리에서는 뺍니다 */
    hideDay?: boolean;
    /** 좋아요·댓글로 숫자가 바뀌었을 때 목록을 새로 받게 합니다 */
    onChanged?: () => void;
    /* 더보기 메뉴 — 모달은 화면이 들고 있습니다. 카드마다 달면 목록 수만큼
       늘어납니다. 하나도 안 넘기면 더보기 버튼 자체가 안 나옵니다. */
    onEdit?: (entry: JournalEntry) => void;
    onDelete?: (entry: JournalEntry) => void;
    onReport?: (entry: JournalEntry) => void;
    onBlock?: (entry: JournalEntry) => void;
}

export const JournalEntryCard: React.FC<Props> = ({
    entry, hideDay, onChanged, onEdit, onDelete, onReport, onBlock,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [comments, setComments] = useState<Comment[]>([]);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);
    const [liked, setLiked] = useState(!!entry.liked);
    const [likeCount, setLikeCount] = useState(entry.likeCount || 0);
    /** 크게 보기로 열어 둔 장수. null 이면 닫힌 상태 */
    const [viewer, setViewer] = useState<number | null>(null);
    const [menu, setMenu] = useState(false);

    /* 내 글이면 고치기·지우기, 남의 글이면 신고·차단. 넘겨받은 것이 하나도
       없으면 버튼을 그리지 않습니다 — 눌러도 안 되는 버튼을 두지 않습니다. */
    const canManage = entry.mine ? !!(onEdit || onDelete) : !!(onReport || onBlock);

    const photos = entry.photos || [];

    const toggleComments = useCallback(async () => {
        const next = !open;
        setOpen(next);
        if (next && comments.length === 0) {
            const res = await PlaceReviewRepository.getReviewComments(entry.reviewId);
            setComments(unwrapList<Comment>(res));
        }
    }, [open, comments.length, entry.reviewId]);

    const toggleLike = useCallback(async () => {
        /* 먼저 화면을 바꾸고 서버에 알립니다 — 눌렀는데 반응이 없으면 또 누릅니다 */
        const next = !liked;
        setLiked(next);
        setLikeCount(c => c + (next ? 1 : -1));
        try {
            await PlaceReviewRepository.toggleLike(entry.reviewId);
        } catch {
            setLiked(!next);
            setLikeCount(c => c + (next ? -1 : 1));
        }
    }, [liked, entry.reviewId]);

    const addComment = useCallback(async () => {
        const text = draft.trim();
        if (!text || busy) return;
        setBusy(true);
        try {
            await PlaceReviewRepository.addReviewComment(entry.reviewId, text);
            const res = await PlaceReviewRepository.getReviewComments(entry.reviewId);
            setComments(unwrapList<Comment>(res));
            setDraft('');
            onChanged?.();
        } finally {
            setBusy(false);
        }
    }, [draft, busy, entry.reviewId, onChanged]);

    return (
        <li className={styles.card}>
            {/* 머리 — 누가 · 어디 · 언제 */}
            <div className={styles.head}>
                <span className={styles.who}>
                    <span className={styles.name}>{entry.nickname || ''}</span>
                    <span className={styles.where}>
                        <IcPin size={11} />
                        {!hideDay && entry.dayNumber ? `${entry.dayNumber}${t('journal.dayUnit')} · ` : ''}
                        {entry.placeName || ''}
                        <span className={styles.dot}>·</span>
                        {formatRelative(entry.createdAt)}
                    </span>
                </span>
                {entry.rating ? <StarRating value={entry.rating} size={13} source="CREW" /> : null}
                {canManage && (
                    <button type="button" className={styles.more}
                            aria-label={t('common.more')} onClick={() => setMenu(true)}>
                        <SvgBase size={16} strokeWidth={2}>
                            <circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" />
                            <circle cx="19" cy="12" r="1" />
                        </SvgBase>
                    </button>
                )}
            </div>

            {photos.length > 0 && (
                <div className={styles.photos}>
                    {photos.length === 1 ? (
                        /* 한 장 — 같은 사진을 두 겹으로 깔아 전체가 보이게 합니다.
                           높이를 못 박으면 세로 사진이 가로 띠로 잘립니다. */
                        <button type="button" className={styles.single}
                                onClick={() => setViewer(0)}>
                            <img src={photos[0].imageUrl} alt="" aria-hidden="true"
                                 className={styles.singleBg} />
                            <img src={photos[0].imageUrl} alt="" loading="lazy"
                                 className={styles.singleImg} />
                        </button>
                    ) : (
                        /* 여러 장 — 디자인 시스템의 3열 그리드를 그대로 씁니다.
                           옆으로 넘기던 예전 방식은 두 번째 장부터 화면 밖이라
                           있는 줄도 몰랐습니다. */
                        <PhotoGrid
                            photos={photos.map(p => ({ id: p.photoId, url: p.imageUrl }))}
                            max={PHOTO_MAX}
                            onSelect={(_, i) => setViewer(i)}
                        />
                    )}
                </div>
            )}

            {/* 크게 보기 — 여기서는 비율을 그대로 살립니다(contain). +N 을 눌러도
                접힌 장까지 전부 넘겨 볼 수 있게 목록 전체를 넘깁니다. */}
            {/* 더보기 — 내 글이면 고치기·지우기, 남의 글이면 신고·차단 */}
            <Sheet open={menu} onClose={() => setMenu(false)}
                   title={t('common.more')} closeLabel={t('common.close')}>
                <ActionList>
                    {entry.mine ? (
                        <>
                            {onEdit && <ActionRow title={t('common.edit')} noArrow
                                                  onClick={() => { setMenu(false); onEdit(entry); }} />}
                            {onDelete && <ActionRow title={t('common.delete')} noArrow danger
                                                    onClick={() => { setMenu(false); onDelete(entry); }} />}
                        </>
                    ) : (
                        <>
                            {onReport && <ActionRow title={t('journal.reportEntry')} noArrow
                                                    onClick={() => { setMenu(false); onReport(entry); }} />}
                            {onBlock && <ActionRow title={t('visitPhoto.block')} noArrow danger
                                                   onClick={() => { setMenu(false); onBlock(entry); }} />}
                        </>
                    )}
                </ActionList>
            </Sheet>

            <PhotoViewerModal
                open={viewer !== null}
                photos={photos.map(p => p.imageUrl)}
                index={viewer ?? 0}
                onClose={() => setViewer(null)}
            />

            {entry.content ? <p className={styles.text}>{entry.content}</p> : null}

            <div className={styles.actions}>
                <button type="button"
                        className={liked ? `${styles.action} ${styles.liked}` : styles.action}
                        onClick={toggleLike}>
                    <IcHeart size={14} /> {likeCount}
                </button>
                <button type="button" className={styles.action} onClick={toggleComments}>
                    <IcChat size={14} /> {entry.commentCount || 0}
                </button>
            </div>

            {open && (
                <div className={styles.comments}>
                    {comments.map(c => (
                        <p key={c.commentId} className={styles.comment}>
                            <b>{c.nickname || ''}</b> {c.content}
                        </p>
                    ))}
                    <div className={styles.commentInput}>
                        <input
                            value={draft}
                            onChange={e => setDraft(e.target.value)}
                            placeholder={t('journal.commentPlaceholder')}
                            onKeyDown={e => { if (e.key === 'Enter') addComment(); }}
                        />
                        <button type="button" onClick={addComment} disabled={!draft.trim() || busy}>
                            {t('journal.commentSubmit')}
                        </button>
                    </div>
                </div>
            )}
        </li>
    );
};
